import { createFileRoute } from '@tanstack/react-router'
import { useRef } from 'react'

import { TaxRate } from '@/api/admin-country-types'
import { Fee } from '@/api/admin-products-types'
import { SalesChannelListItem } from '@/api/admin-sales-channels-types'
import { useUpdateProductGroupFee } from '@/api/api-mutations'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { useRouteModal } from '@/components/route-modal'
import { RouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import {
	FeePricesGrid,
	FeePricesGridRef
} from '@/routes/_authenticated/_general/product-groups/$groupUuid/fees/-components'

export const Route = createFileRoute('/_authenticated/_general/product-groups/$groupUuid/fees/$feeUuid/edit')({
	component: EditProductGroupFee
})

function EditProductGroupFee() {
	const { groupUuid, feeUuid } = Route.useParams()

	return (
		<RouteModal
			asDrawer
			requiresData
			loader={async api =>
				await Promise.all([
					api.getProductGroupFee({ path: { groupUuid, feeUuid } }),
					api.listSalesChannels({}),
					api.listTaxRates({})
				])
			}
		>
			{([fee, salesChannels, taxRates]) => (
				<EditProductGroupFeeForm fee={fee} salesChannelsData={salesChannels} taxRatesData={taxRates} />
			)}
		</RouteModal>
	)
}

function EditProductGroupFeeForm({
	fee,
	salesChannelsData,
	taxRatesData
}: {
	fee: Fee
	salesChannelsData: SalesChannelListItem[]
	taxRatesData: TaxRate[]
}) {
	const { L } = useI18n()
	const { groupUuid, feeUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()
	const gridRef = useRef<FeePricesGridRef>(null)

	const updateProductGroupFee = useUpdateProductGroupFee()

	const form = useAppForm({
		defaultValues: {
			name: fee.name ?? ''
		},
		onSubmit: async ({ value }) => {
			const prices = gridRef.current?.getGridData() ?? []
			if (prices.length === 0) {
				toast.error(L.ProductGroup.Fees.Create.NoPrices())
				return
			}

			const result = await updateProductGroupFee({
				path: { groupUuid, feeUuid },
				body: { name: value.name, prices }
			})
			if (result.isSuccess) {
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<div className='flex size-full flex-col'>
				<Drawer.Header>
					<Drawer.Title>
						<Heading>{L.ProductGroup.Fees.Edit?.Title?.() || L.ProductGroup.Fees.Create.Title()}</Heading>
					</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body className='flex-1 gap-y-4 overflow-auto'>
					<FormContainer>
						<FormFieldGroup>
							<form.AppField
								name='name'
								children={field => <field.TextField label={L.Fields.Name()} />}
							/>
						</FormFieldGroup>
					</FormContainer>
					<Container>
						<ContainerHeader heading={L.ProductGroup.Fees.Create.Pricing()} />
						<FeePricesGrid
							ref={gridRef}
							initialPrices={fee.prices}
							salesChannelsData={salesChannelsData}
							taxRatesData={taxRatesData}
						/>
					</Container>
				</Drawer.Body>
				<Drawer.Footer>
					<div className='flex w-full items-center justify-end gap-x-2'>
						<Drawer.Close asChild>
							<Button variant='secondary' size='small'>
								{L.General.Cancel()}
							</Button>
						</Drawer.Close>
						<form.SaveButton />
					</div>
				</Drawer.Footer>
			</div>
		</form.AppForm>
	)
}
